/**
 * PillMultiSelect — a multi-select pill group with an optional "+custom…" entry
 * (E06-F01-T07). One control covers the Research-Intake `companyTypes` and
 * `fundingStages` fields: a row of toggleable preset pills plus a free-text input
 * that adds a custom pill (active/selected by default) on Enter or comma
 * (`addCustomPill` parity).
 *
 * Selection model — controlled via `selected`/`onChange`:
 *   - clicking a preset pill toggles it in/out of `selected`;
 *   - typing a value + Enter/comma in the custom input appends it to BOTH the
 *     rendered pill set (so it shows as a pill) AND `selected` (active by
 *     default — T07 acceptance #2); a custom pill that duplicates a preset just
 *     selects the preset;
 *   - clicking a custom pill toggles its selected state like any other (#3);
 *   - the × on a custom pill removes it entirely (presets cannot be removed,
 *     only deselected).
 *
 * `selected` is the source of truth for what gets persisted into criteria. The
 * custom pills the user has added (but maybe deselected) are tracked internally
 * so a deselected custom pill stays visible to re-toggle. Presentational; brand
 * tokens only (no raw hex).
 */
import { useMemo, useState, type JSX, type KeyboardEvent } from 'react';

export interface PillMultiSelectProps {
  /** Preset options always shown as pills (cannot be removed, only toggled). */
  readonly presets: readonly string[];
  /** Currently-selected values (controlled) — the criteria source of truth. */
  readonly selected: readonly string[];
  /** Fired with the next selected array on every toggle / custom add. */
  readonly onChange: (next: string[]) => void;
  /** Placeholder for the custom-entry input. */
  readonly customPlaceholder?: string;
  /** Accessible label / group name (e.g. "Company type"). */
  readonly ariaLabel: string;
  /** Optional test id on the root container. */
  readonly testId?: string;
  /** Disable all interaction. */
  readonly disabled?: boolean;
}

/** Active pill — gold accent fill on navy text (brand accent token). */
const ACTIVE_PILL =
  'bg-[var(--color-accent)] text-[var(--color-primary)] border-[var(--color-accent)]';
/** Idle pill — surface with muted border. */
const IDLE_PILL =
  'bg-white text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-primary)]';

/** Case-insensitive membership (so "SaaS" and "saas" don't double-add). */
function includesCi(arr: readonly string[], v: string): boolean {
  const low = v.toLowerCase();
  return arr.some((x) => x.toLowerCase() === low);
}

export function PillMultiSelect({
  presets,
  selected,
  onChange,
  customPlaceholder = '+custom…',
  ariaLabel,
  testId = 'pill-multi-select',
  disabled = false,
}: PillMultiSelectProps): JSX.Element {
  const [draft, setDraft] = useState('');
  // Custom pills the user added that are NOT in the presets — kept so a
  // deselected custom value stays visible (re-toggleable).
  const [customPills, setCustomPills] = useState<string[]>([]);

  // The full rendered pill list = presets ∪ any custom-added (and any selected
  // value not already covered, e.g. seeded from a saved draft).
  const allPills = useMemo(() => {
    const out = [...presets];
    for (const c of customPills) if (!includesCi(out, c)) out.push(c);
    for (const s of selected) if (!includesCi(out, s)) out.push(s);
    return out;
  }, [presets, customPills, selected]);

  function toggle(value: string): void {
    if (disabled) return;
    if (includesCi(selected, value)) {
      onChange(selected.filter((s) => s.toLowerCase() !== value.toLowerCase()));
    } else {
      onChange([...selected, value]);
    }
  }

  function removeCustom(value: string): void {
    if (disabled) return;
    setCustomPills((prev) => prev.filter((c) => c.toLowerCase() !== value.toLowerCase()));
    if (includesCi(selected, value)) {
      onChange(selected.filter((s) => s.toLowerCase() !== value.toLowerCase()));
    }
  }

  /** addCustomPill: append + auto-select (active by default — T07 #2). */
  function commitCustom(): void {
    const value = draft.trim().replace(/,+$/, '').trim();
    setDraft('');
    if (!value) return;
    // If it matches a preset, just select the preset (no duplicate custom pill).
    if (!includesCi(presets, value) && !includesCi(customPills, value)) {
      setCustomPills((prev) => [...prev, value]);
    }
    if (!includesCi(selected, value)) onChange([...selected, value]);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitCustom();
    }
  }

  const isCustom = (value: string): boolean => !includesCi(presets, value);

  return (
    <div data-testid={testId} role="group" aria-label={ariaLabel} className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {allPills.map((pill) => {
          const active = includesCi(selected, pill);
          return (
            <span
              key={pill}
              data-testid={`${testId}-pill`}
              data-pill-value={pill}
              data-active={active ? 'true' : 'false'}
              className={`inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full border transition-colors ${
                active ? ACTIVE_PILL : IDLE_PILL
              }`}
            >
              <button
                type="button"
                disabled={disabled}
                aria-pressed={active}
                className="outline-none focus-visible:underline disabled:opacity-50"
                onClick={() => toggle(pill)}
              >
                {pill}
              </button>
              {isCustom(pill) && !disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${pill}`}
                  title="Remove"
                  className="leading-none font-bold text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                  onClick={() => removeCustom(pill)}
                >
                  {'×'}
                </button>
              )}
            </span>
          );
        })}
      </div>
      <input
        type="text"
        className="border border-[var(--color-border)] rounded-md px-2.5 py-1.5 text-sm w-44 outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
        placeholder={customPlaceholder}
        aria-label={`${ariaLabel} — add custom`}
        data-testid={`${testId}-custom-input`}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commitCustom}
      />
    </div>
  );
}
